import { useEffect, useRef, useState } from "react";
import { pushStatus, pushSubscribe, pushUnsubscribe, pushTest, pushGetStatus, pushSetDigestMode, pushTestAlert, smsSubscribe, smsUnsubscribe, smsTest, smsList } from "../utils/alerts";
import { hapticTap, hapticSuccess, hapticError } from "../utils/haptics";

/**
 * AlertsCard — single settings surface to manage push + SMS subscriptions.
 * Drop this anywhere in the Settings tab.
 */
export default function AlertsCard() {
  const [push, setPush] = useState({ supported: false, subscribed: false, permission: "default" });
  const [digestMode, setDigestMode] = useState(false);
  const [phone, setPhone] = useState("");
  const [smsSubs, setSmsSubs] = useState([]);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");
  const flashRef = useRef(null);

  useEffect(() => () => { if (flashRef.current) clearTimeout(flashRef.current); }, []);

  const reloadPush = async () => {
    const s = await pushStatus().catch(() => null);
    if (s) setPush(s);
    if (s?.endpoint) {
      const st = await pushGetStatus(s.endpoint);
      if (st?.found) setDigestMode(!!st.digestMode);
    }
  };
  const reloadSms = () => smsList().then(d => setSmsSubs(d.subscribers || [])).catch(() => {});

  useEffect(() => { reloadPush(); reloadSms(); }, []);

  const flash = (msg, ok = true) => {
    setToast(msg);
    if (ok) hapticSuccess(); else hapticError();
    if (flashRef.current) clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setToast(""), 2500);
  };

  const onSub = async () => {
    hapticTap(); setBusy("push-sub");
    try { await pushSubscribe(); await reloadPush(); flash("Push enabled"); }
    catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };
  const onUnsub = async () => {
    hapticTap(); setBusy("push-unsub");
    try { await pushUnsubscribe(); await reloadPush(); flash("Push disabled"); }
    catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };
  const onTest = async () => {
    hapticTap(); setBusy("push-test");
    try { const r = await pushTest(); flash(r.ok ? `Sent to ${r.sent} device(s)` : "Send failed", r.ok); }
    catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };

  const onDigestToggle = async (next) => {
    if (!push.endpoint) { flash("Subscribe to push first", false); return; }
    hapticTap(); setBusy("digest-toggle");
    try {
      await pushSetDigestMode(push.endpoint, next);
      setDigestMode(next);
      flash(next ? "Digest mode ON · alerts bundled every 5 min" : "Digest mode OFF · instant alerts");
    } catch (e) { flash(e.response?.data?.detail || e.message || "Failed", false); }
    setBusy("");
  };

  const onTestAlert = async () => {
    hapticTap(); setBusy("digest-test");
    try {
      const r = await pushTestAlert();
      if (r.queued > 0) flash(`Queued · will arrive in next digest (≤5 min)`);
      else if (r.sent > 0) flash(`Sent immediately to ${r.sent} device(s)`);
      else flash("No subscribers", false);
    } catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };

  const onAddPhone = async () => {
    hapticTap();
    const trimmed = phone.trim();
    if (!trimmed.startsWith("+")) { flash("Use E.164 format (+1...)", false); return; }
    setBusy("sms-sub");
    try { await smsSubscribe(trimmed); await reloadSms(); setPhone(""); flash("Phone added"); }
    catch (e) { flash(e.response?.data?.detail || e.message || "Failed", false); }
    setBusy("");
  };
  const onRemovePhone = async (p) => {
    hapticTap(); setBusy(`sms-${p}`);
    try { await smsUnsubscribe(p); await reloadSms(); flash("Removed"); }
    catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };
  const onSmsTest = async (p) => {
    hapticTap(); setBusy(`sms-test-${p}`);
    try {
      const r = await smsTest(p);
      flash(r.ok ? "SMS sent" : (r.error || "Failed"), r.ok);
    }
    catch (e) { flash(e.message || "Failed", false); }
    setBusy("");
  };

  const btn = (color, disabled = false) => ({
    padding: "8px 14px", borderRadius: 20,
    background: disabled ? "#636e7b" : color,
    color: "#fff", fontSize: 12, fontWeight: 700, border: "none",
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
  });

  return (
    <div className="card" data-testid="alerts-card" style={{ borderColor: "rgba(96,165,250,0.2)" }}>
      <div className="sh" style={{ marginBottom: 12 }}>Alert Channels</div>

      {/* Web Push */}
      <div style={{ padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>Web Push</div>
            <div style={{ fontSize: 10, color: "var(--dim)" }}>
              {!push.supported ? "Not supported on this device" :
               push.subscribed ? "Active on this device" :
               push.permission === "denied" ? "Blocked — enable in browser settings" : "Not enabled"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {push.subscribed ? (
              <>
                <button data-testid="push-test-btn" disabled={busy === "push-test"} onClick={onTest} style={btn("#60a5fa", busy === "push-test")}>Test</button>
                <button data-testid="push-unsub-btn" disabled={busy === "push-unsub"} onClick={onUnsub} style={btn("#ef4444", busy === "push-unsub")}>Disable</button>
              </>
            ) : (
              <button data-testid="push-sub-btn" disabled={!push.supported || busy === "push-sub" || push.permission === "denied"} onClick={onSub} style={btn("#16a34a", !push.supported || busy === "push-sub" || push.permission === "denied")}>Enable</button>
            )}
          </div>
        </div>

        {/* Digest mode — only visible when subscribed */}
        {push.subscribed && (
          <div data-testid="digest-mode-row" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#e6edf3", display: "flex", alignItems: "center", gap: 6 }}>
                Digest Mode
                {digestMode && <span style={{ fontSize: 8, padding: "1px 6px", borderRadius: 4, background: "rgba(167,139,250,0.18)", color: "#a78bfa", fontWeight: 700 }}>ON</span>}
              </div>
              <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 2 }}>
                {digestMode
                  ? "Risk alerts are bundled into one push every 5 min"
                  : "Every alert fires an immediate push"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                data-testid="digest-test-alert-btn"
                disabled={busy === "digest-test"}
                onClick={onTestAlert}
                style={btn("#a78bfa", busy === "digest-test")}
              >Test Alert</button>
              <button
                data-testid="digest-toggle-btn"
                disabled={busy === "digest-toggle"}
                onClick={() => onDigestToggle(!digestMode)}
                style={btn(digestMode ? "#636e7b" : "#a78bfa", busy === "digest-toggle")}
              >{digestMode ? "Turn Off" : "Turn On"}</button>
            </div>
          </div>
        )}
      </div>

      {/* SMS */}
      <div style={{ padding: 12, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", marginBottom: 4 }}>SMS Alerts</div>
        <div style={{ fontSize: 10, color: "var(--dim)", marginBottom: 10 }}>Twilio · Trail stops, risk gates, morning briefing</div>

        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <input
            data-testid="sms-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+15551234567"
            style={{ flex: 1, padding: "8px 10px", borderRadius: 8, background: "#0d1117", border: "1px solid rgba(255,255,255,0.08)", color: "#e6edf3", fontSize: 12 }}
          />
          <button data-testid="sms-add-btn" disabled={busy === "sms-sub"} onClick={onAddPhone} style={btn("#16a34a", busy === "sms-sub")}>Add</button>
        </div>

        {smsSubs.length === 0 ? (
          <div style={{ fontSize: 10, color: "var(--dim)", textAlign: "center", padding: 8 }}>No phone numbers added</div>
        ) : (
          smsSubs.map((s) => (
            <div key={s.phone} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.04)" }} data-testid={`sms-row-${s.phone}`}>
              <span style={{ fontSize: 12, color: "#e6edf3", fontFamily: "monospace" }}>{s.phone}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button disabled={busy === `sms-test-${s.phone}`} onClick={() => onSmsTest(s.phone)} style={btn("#60a5fa", busy === `sms-test-${s.phone}`)}>Test</button>
                <button disabled={busy === `sms-${s.phone}`} onClick={() => onRemovePhone(s.phone)} style={btn("#ef4444", busy === `sms-${s.phone}`)}>Remove</button>
              </div>
            </div>
          ))
        )}
      </div>

      {toast && <div data-testid="alerts-toast" style={{ marginTop: 10, padding: 8, borderRadius: 8, background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", fontSize: 11, color: "#60a5fa", textAlign: "center" }}>{toast}</div>}
    </div>
  );
}
